import { useEffect } from 'react';

interface UseWeekOffDaysFilteringProps {
  weekOffDays: string[];
  isRegularEngagement: boolean;
  setTableRowsByFreelancer: React.Dispatch<React.SetStateAction<any>>;
  calendarSelectedDatesByFreelancer: { [key: string]: Date[] };
  setCalendarSelectedDatesByFreelancer: React.Dispatch<React.SetStateAction<{ [key: string]: Date[] }>>;
  setValue: (name: string, value: any) => void;
}

/**
 * Custom hook to handle week-off days filtering
 * Filters out table rows and calendar dates that fall on selected week-off days
 * Only applies for regular engagement type
 */
export const useWeekOffDaysFiltering = ({
  weekOffDays,
  isRegularEngagement,
  setTableRowsByFreelancer,
  calendarSelectedDatesByFreelancer,
  setCalendarSelectedDatesByFreelancer,
  setValue
}: UseWeekOffDaysFilteringProps) => {
  
  // Re-filter dates when week-off days change
  useEffect(() => {
    if (!isRegularEngagement || weekOffDays.length === 0) {
      return; // No need to re-filter if not regular engagement or no week-off days
    }

    // Helper function to check if a date is a week-off day
    const isWeekOffDay = (date: Date): boolean => {
      const dayOfWeek = date.getDay();
      const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
      const dayName = dayNames[dayOfWeek];
      return weekOffDays.includes(dayName);
    };

    // Re-filter dates for all freelancers
    setTableRowsByFreelancer((prevRowsByFreelancer: any) => {
      const updatedRows: typeof prevRowsByFreelancer = {};

      Object.keys(prevRowsByFreelancer).forEach(freelancerId => {
        const rows = prevRowsByFreelancer[freelancerId] || [];
        
        // Filter out rows that fall on week-off days
        const filteredRows = rows.filter((row: any) => {
          if (!row.date) return true; // Keep rows without dates
          
          // Parse the date string back to Date object
          const dateParts = row.date.split('/');
          if (dateParts.length !== 3) return true; // Invalid format, keep it
          
          const [month, day, year] = dateParts;
          const rowDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          
          return !isWeekOffDay(rowDate);
        });

        updatedRows[freelancerId] = filteredRows;

        // Update calendar selected dates
        const calendarDates = calendarSelectedDatesByFreelancer[freelancerId] || [];
        const filteredCalendarDates = calendarDates.filter(date => !isWeekOffDay(date));
        
        setCalendarSelectedDatesByFreelancer(prev => ({
          ...prev,
          [freelancerId]: filteredCalendarDates
        }));

        // Update workDates display
        if (filteredRows.length > 0 && freelancerId === 'default') {
          const dates = filteredRows.map((row: any) => row.date).filter(Boolean);
          if (dates.length > 0) {
            const displayText = dates.length > 1
              ? `${dates[0]} - ${dates[dates.length - 1]}`
              : dates[0];
            setValue('workDates', displayText);
          } else {
            setValue('workDates', 'No working days selected');
          }
        }
      });

      return updatedRows;
    });
  }, [weekOffDays, isRegularEngagement, setTableRowsByFreelancer, calendarSelectedDatesByFreelancer, setCalendarSelectedDatesByFreelancer, setValue]);
};
